import React, { useState } from 'react';
import { Container, Typography, Box, Accordion, AccordionSummary, AccordionDetails, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export default function FaqAccordion() {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqs = [
    {
      id: 'panel1',
      question: "What is CoreTechTalks?",
      answer: "CoreTechTalks is a technical platform dedicated to sharing high-quality programming tutorials, development insights, cloud automation strategies, and software QA guides. In addition, we build and provide developers with utility applications."
    },
    {
      id: 'panel2',
      question: "What utility tools are hosted on CoreTechTalks?",
      answer: "We currently provide the Rent Agreement Red Flag Detector (designed to analyze and audit draft Indian tenancy agreements) and feature access to PDF Toolkit (a client-side suite to merge, split, compress, and edit PDF documents)."
    },
    {
      id: 'panel3',
      question: "Is my uploaded data secure when using your tools?",
      answer: "Yes, privacy is a fundamental design principle. All processing for our web tools occurs locally within secure temporary browser memory. Your documents are never stored or shared."
    },
    {
      id: 'panel4',
      question: "How frequently are new guides and utilities published?",
      answer: "We update the site regularly with fresh engineering publications, automated scripting architectures, and intelligent web utilities to help simplify developer workflows."
    }
  ];

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 5 }}>
        <Box 
          sx={{ 
            display: 'inline-flex',
            p: 1.5,
            borderRadius: '50%',
            bgcolor: 'rgba(99, 102, 241, 0.1)',
            color: mode === 'light' ? '#4F46E5' : '#818CF8',
            mb: 2
          }}
        >
          <HelpOutlineIcon sx={{ fontSize: '28px' }} />
        </Box>
        <Typography 
          variant="h3" 
          component="h2" 
          sx={{ mb: 1, fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '500px' }}>
          Frequently asked questions about the CoreTechTalks platform, technical blogs, and utility tools.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {faqs.map((faq) => (
          <Accordion
            key={faq.id}
            expanded={expanded === faq.id}
            onChange={handleChange(faq.id)}
            sx={{
              borderRadius: '12px !important',
              border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
              bgcolor: mode === 'light' ? '#FFFFFF' : 'rgba(11, 15, 25, 0.5)',
              boxShadow: expanded === faq.id ? '0 8px 24px rgba(99, 102, 241, 0.08)' : 'none',
              '&::before': { display: 'none' },
              transition: 'all 0.3s ease',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: expanded === faq.id ? 'primary.main' : 'text.secondary' }} />}
              sx={{
                px: 3,
                py: 1,
                fontWeight: 650,
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.02)' },
                borderRadius: '12px',
              }}
            >
              <Typography sx={{ fontWeight: 650, fontSize: '15px' }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3, pt: 1, borderTop: `1px solid ${mode === 'light' ? '#F1F5F9' : 'rgba(255,255,255,0.03)'}` }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '14px' }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Container>
  );
}
